import { collection } from '@/infra/collection'
import { JobGist, type JobGistEvent } from '@/features/jobs/JobGist'
import DataLoader from 'dataloader'
import { type JobsApi } from '@/features/jobs/api/job-api'
import { mapJobProtoToGistProps } from '@/features/jobs/job-mappers'
import { Id } from '@/infra/id'
import { map } from 'rxjs'
import { filterNonNil } from '@/utils/rx-util'
import { share } from 'rxjs/operators'
import { type RealtimeEvent } from '@taxfyle/api-internal/internal/realtime_pb'
import type { JobDataLoader } from '@/features/jobs/JobDataLoader'
import type { FetchJobOptions } from '@/features/jobs'

/**
 * Manages job gists, which are minimal representations of jobs that
 * can be referenced in other features that just require the gist of the job.
 */
export class JobGistStore {
  private readonly _loader = new DataLoader(this._fetchManyCore.bind(this))

  /**
   * Tracks which jobs have been invalided. When attempting to fetch,
   * if the ID is present here, we'll force a refetch.
   *
   * @private
   */
  private readonly _invalidated = new Set<string>()

  /**
   * Collection of job gists.
   */
  readonly jobGists = collection({
    getModelId: (m) => m.id,
    getDataId: (m) => m.id,
    create: () => new JobGist(),
  })

  /**
   * Emits job gists as they are updated in response to real-time events.
   */
  readonly realtime$

  constructor(
    private readonly _api: JobsApi,
    private readonly _jobDataLoader: JobDataLoader,
  ) {
    // When the inner job loader clears an entry, clear it in our loader
    // as well.
    this._jobDataLoader.clear$.subscribe((id) => {
      this._loader.clear(id)
      this._invalidated.add(id)
    })

    this.realtime$ = this._api.realtime$.pipe(
      map((evt) => this._receiveRealtimeEvent(evt)),
      filterNonNil(),
      share(),
    )

    this.fetchJobGist = this.fetchJobGist.bind(this)
  }

  /**
   * Fetches the gist of a job.
   *
   * @param id
   * @param options
   */
  async fetchJobGist(id: Id, options?: FetchJobOptions) {
    if (!Id.some(id)) {
      return null
    }

    if (options?.force) {
      this._jobDataLoader.clear(id)
    }

    const invalidated = this._invalidated.has(id)
    if (invalidated) {
      this._invalidated.delete(id)
    }

    const existing = this.jobGists.get(id)
    if (existing && !invalidated) {
      // NOTE: Dataloader caches as well, but it results in
      // a promise that is slow enough to cause skeleton flashing
      // in the UI even when the value has already been retrieved.
      return existing
    }

    return await this._loader.load(id)
  }

  /**
   * Fetches the jobs by IDs.
   *
   * @param ids
   * @private
   */
  private async _fetchManyCore(
    ids: readonly string[],
  ): Promise<(JobGist | null)[]> {
    const response = await this._jobDataLoader.fetchMany(ids)
    const props = response.map((resource) => {
      return !resource ? null : mapJobProtoToGistProps(resource)
    })

    return this.jobGists.set(props)
  }

  /**
   * Receives a realtime event and syncs to the collection.
   * Returns the relevant model.
   *
   * @param evt
   * @private
   */
  private _receiveRealtimeEvent(evt: RealtimeEvent): JobGistEvent | null {
    const jobProto =
      evt.getJobUpdated()?.getJob() ?? evt.getJobCreated()?.getJob()
    if (!jobProto) {
      return null
    }

    const jobGist = this.jobGists.set(mapJobProtoToGistProps(jobProto))
    if (evt.getJobCreated()) {
      return {
        type: 'Created',
        jobGist,
      }
    }

    return {
      type: 'Updated',
      jobGist,
    }
  }
}
